import { useState } from "react";
import { DocumentCategory } from "@/models/legislation/Legislation";
import useUsers from "@/hooks/useUsers";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import useFavoriteOperationStore from "@/stores/favorites.store";

export type FavoriteToggleParams = {
  documentCategory: DocumentCategory;
  documentId: string;
  title: string;
  isFavorite: boolean;
  datasource?: string;
  internalId?: string;
  workspaceId?: string;
};

export type FavoriteToggleMutations = {
  addFavoriteLegislation: (request: {
    externalId: string;
    datasource: string;
    internalId?: string;
    title: string;
  }) => Promise<unknown>;
  removeFavoriteLegislation: (request: {
    externalId: string;
  }) => Promise<unknown>;
  addFavoriteWorkspaceDocument: (request: {
    workspaceId: string;
    documentId: string;
    title: string;
  }) => Promise<unknown>;
  removeFavoriteWorkspaceDocument: (request: {
    documentId: string;
  }) => Promise<unknown>;
};

/**
 * Pure favorite toggle used by the hook and unit tests.
 */
export async function executeFavoriteToggle(
  params: FavoriteToggleParams,
  mutations: FavoriteToggleMutations
): Promise<void> {
  const {
    documentCategory,
    documentId,
    title,
    isFavorite,
    datasource,
    internalId,
    workspaceId,
  } = params;

  if (documentCategory === DocumentCategory.Legislation) {
    if (!datasource) {
      throw new Error("Datasource er påkrævet for lovgivning");
    }
    if (isFavorite) {
      await mutations.removeFavoriteLegislation({ externalId: documentId });
    } else {
      await mutations.addFavoriteLegislation({
        externalId: documentId,
        datasource,
        internalId,
        title,
      });
    }
    return;
  }

  if (documentCategory === DocumentCategory.Workspace) {
    if (!workspaceId) {
      throw new Error("Workspace ID er påkrævet for workspace dokumenter");
    }
    if (isFavorite) {
      await mutations.removeFavoriteWorkspaceDocument({ documentId });
    } else {
      await mutations.addFavoriteWorkspaceDocument({
        workspaceId,
        documentId,
        title,
      });
    }
    return;
  }

  throw new Error(`Ukendt dokumentkategori: ${documentCategory}`);
}

type UseFavoriteToggleOptions = FavoriteToggleParams & {
  onOperationStart?: () => void;
  onOperationEnd?: () => void;
};

export function useFavoriteToggle({
  onOperationStart,
  onOperationEnd,
  ...params
}: UseFavoriteToggleOptions) {
  const {
    addFavoriteLegislation,
    addFavoriteWorkspaceDocument,
    removeFavoriteLegislation,
    removeFavoriteWorkspaceDocument,
    favoritizingLoading,
  } = useUsers();

  const [isMutating, setIsMutating] = useState(false);
  // Subscribe so store pending updates re-render (getState() alone does not).
  const isStorePending = useFavoriteOperationStore((s) =>
    s.pendingOperations.has(params.documentId)
  );

  const isLoading =
    isMutating ||
    isStorePending ||
    favoritizingLoading(params.documentId);
  const label = params.isFavorite ? "Fjern favorit" : "Favoriser";

  const toggle = async () => {
    setIsMutating(true);
    onOperationStart?.();
    try {
      await executeFavoriteToggle(params, {
        addFavoriteLegislation: (request) =>
          addFavoriteLegislation.mutateAsync(request),
        removeFavoriteLegislation: (request) =>
          removeFavoriteLegislation.mutateAsync(request),
        addFavoriteWorkspaceDocument: (request) =>
          addFavoriteWorkspaceDocument.mutateAsync(request),
        removeFavoriteWorkspaceDocument: (request) =>
          removeFavoriteWorkspaceDocument.mutateAsync(request),
      });
    } catch (error) {
      logger.error("Failed to update favorite", {
        operation: "toggleFavorite",
        error,
        documentCategory: params.documentCategory,
        documentId: params.documentId,
        workspaceId: params.workspaceId,
        datasource: params.datasource,
      });
      showErrorToast(
        "Der opstod en fejl ved opdatering af favorit: " +
          (error as Error).message
      );
    } finally {
      setIsMutating(false);
      onOperationEnd?.();
    }
  };

  return { toggle, isLoading, label };
}

export default useFavoriteToggle;
